import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  Fab,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import SaveIcon from "@material-ui/icons/Save";
import { default as lodash, default as _ } from "lodash";
import { useSnackbar } from "notistack";
import React from "react";
import { useHistory } from "react-router";
import TestImporter from "../components/CSV/TestImporter";
import CompanyFilter from "../components/Filters/CompanyFilter";
import DateFilter from "../components/Filters/DateFilter";
import LocationFilter from "../components/Filters/LocationFilter";
import UserFilter from "../components/Filters/UserFilter";
import { SearchField } from "../components/Search";
import ResendDialog from "../components/Tests/ResendMutlipleDialog";
import TestTable from "../components/Tests/TestTable";
import { ROUTES } from "../constants";
import { Test } from "../graphql/baseTypes";
import { SEARCH_TESTS } from "../graphql/queries";
import { SearchTestsQuery } from "../graphql/queries/searchTests";
import {
  SearchableTestFilterInput,
  SearchTestsQueryVariables,
} from "../graphql/types";
import useSiteTitle from "../hooks/useSiteTitle";
import useFilters, { Filters } from "../hooks/useTestFilters";
import useUser from "../hooks/useUser";
import { generateLongIdFromTest, parseDate } from "../utils/tests";

interface Props {}

function generateFilter(state: Filters): SearchableTestFilterInput | undefined {
  const and: Array<SearchableTestFilterInput> = [];
  if (state.companyId) {
    and.push({
      companyId: { eq: state.companyId },
    });
  }
  if (state.locationId) {
    and.push({
      locationId: { eq: state.locationId },
    });
  }
  if (state.userId) {
    and.push({
      userId: { eq: state.userId },
    });
  }
  if (state.endDate) {
    and.push({
      date: { lte: parseDate(state.endDate, true) },
    });
  }
  if (state.startDate) {
    and.push({
      date: { gte: parseDate(state.startDate) },
    });
  }
  if (and.length === 0) {
    return undefined;
  }
  return {
    and,
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  filter: { marginTop: 40, marginBottom: 20 },
  button: {
    margin: theme.spacing(1),
  },
}));

const Tests: React.FC<Props> = () => {
  const classes = useStyles();

  const [selected, setSelected] = React.useState<string[]>([]);
  const resetSelectedItems = React.useCallback(() => setSelected([]), []);
  const [resendOpen, setResendOpen] = React.useState<boolean>(false);
  const history = useHistory();
  const [showImport, setShowImport] = React.useState<boolean>(false);
  const { user, loading: userLoading } = useUser();
  const {
    state,
    page: { page, rowsPerPage },
    setSelection,
    applieFilters,
    resetFilters,
    resetSelection,
    appliedState,
    setPage,
    setRowsPerPage,
    setSearchValue,
    searchValue,
  } = useFilters(resetSelectedItems);
  const { enqueueSnackbar } = useSnackbar();
  useSiteTitle("Testübersicht");
  const { data, loading, networkStatus, fetchMore, refetch } = useQuery<
    SearchTestsQuery,
    SearchTestsQueryVariables
  >(SEARCH_TESTS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      ...(state.filtersApplie && {
        filter: generateFilter(appliedState),
      }),
      ...(searchValue && { search: searchValue }),

      limit: rowsPerPage,
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden der Tests", {
        variant: "error",
      });
    },
  });

  const { searchTests } = data || {};
  const { items, total } = searchTests || {};

  const tests = React.useMemo(
    () =>
      (items || []).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [items, page, rowsPerPage]
  );

  const _handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
    fetchMore({
      variables: {
        ...(state.filtersApplie && {
          filter: generateFilter(appliedState),
        }),
        ...(searchValue && { search: searchValue }),

        from: newPage * rowsPerPage,
        limit: rowsPerPage,
      },
      updateQuery: (prev, { fetchMoreResult: { searchTests } = {} }) => {
        if (!searchTests) return prev;
        return Object.assign({}, prev, {
          searchTests: {
            ...prev.searchTests,
            items: lodash.uniqWith(
              (prev.searchTests.items || []).concat(searchTests.items || []),
              lodash.isEqual
            ),
          },
        });
      },
    });
  };

  const _handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      const newSelecteds = tests.map(
        (row) => `${row.locationId}#${row.testId}#${row.date}`
      );
      setSelected(newSelecteds);
      return;
    }
    resetSelectedItems();
  };
  const _handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(+event.target.value);
  };
  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };
  const _handleTestSearch = lodash.debounce(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchValue(event.target.value);
    },
    500
  );

  const _handleCleareSearch = () => {
    setSearchValue("");
  };

  const __handleSendMailsToTestPersons = () => {
    setResendOpen(true);
  };

  const handleNavigate = (id: string) => {
    const [locationId, testId, date] = id.split("#");
    history.push(`${ROUTES.TESTS}/${locationId}/${testId}/${date}`);
  };
  const _handleResendClose = () => {
    setResendOpen(false);
  };

  const selectedTests: Array<Test> = React.useMemo(() => {
    const lookUp = _.keyBy(tests, (test) => generateLongIdFromTest(test));

    return selected.map((id) => lookUp[id]);
  }, [selected, tests]);

  const _handleFinishImport = () => {
    refetch({
      ...(state.filtersApplie && {
        filter: generateFilter(appliedState),
      }),
      ...(searchValue && { search: searchValue }),
      limit: rowsPerPage,
    });
    setShowImport(false);
  };

  const _onImport = () => {
    setShowImport((value) => !value);
  };

  return (
    <div className={classes.root}>
      {resendOpen && (
        <ResendDialog
          ids={selectedTests}
          open={true}
          handleClose={_handleResendClose}
        />
      )}
      <div className={classes.header}>
        <Typography variant="h3">Testübersicht</Typography>
        <Tooltip title="Testdaten einlesen">
          <Fab
            aria-label="create employee"
            size={"medium"}
            disabled={loading || userLoading}
            onClick={_onImport}
            color={"primary"}
          >
            {!showImport ? <AddIcon /> : <CloseIcon />}
          </Fab>
        </Tooltip>
      </div>
      {showImport && (
        <TestImporter username={user.username} onFinish={_handleFinishImport} />
      )}
      <Grid container className={classes.filter}>
        <Grid item xs={12} md={12} container direction="row" spacing={2}>
          <Grid item>
            <Typography variant={"h6"}>Filter</Typography>
          </Grid>
          <Grid item xs={12} md={12} container justify="space-between">
            <CompanyFilter
              onChange={setSelection("companyId")}
              onClear={resetSelection("companyId", "locationId")}
              value={state.selected.companyId}
            />
            <LocationFilter
              selectedCompany={state.selected.companyId}
              onChange={setSelection("locationId", "companyId")}
              onClear={resetSelection("locationId")}
              value={state.selected.locationId}
            />
            <UserFilter
              onChange={setSelection("userId")}
              onClear={resetSelection("userId")}
              value={state.selected.userId}
            />
            <DateFilter
              label={"Von"}
              tooltipTitle="von 00:00:00 Uhr"
              selection={state.selected.startDate}
              onChange={setSelection("startDate")}
              onClear={resetSelection("startDate")}
            />
            <DateFilter
              label={"Bis"}
              tooltipTitle="bis 23:59:59 Uhr"
              selection={state.selected.endDate}
              onChange={setSelection("endDate")}
              onClear={resetSelection("endDate")}
              maxDate={new Date()}
            />
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          container
          direction="row"
          alignItems="center"
          justify="flex-start"
        >
          {state.changed && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={classes.button}
              onClick={applieFilters}
              startIcon={<SaveIcon />}
            >
              Speichern
            </Button>
          )}
          {state.filtersApplie && (
            <Button
              variant="contained"
              color="secondary"
              onClick={resetFilters}
              size="large"
              className={classes.button}
              startIcon={<RotateLeftIcon />}
            >
              Zurücksetzten
            </Button>
          )}
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        md={12}
        container
        direction="row"
        alignItems="center"
        justify="flex-start"
        className={classes.header}
      >
        <SearchField
          onClearSearch={_handleCleareSearch}
          onSearchChange={_handleTestSearch}
          value={searchValue}
          ignoreLength
        />
      </Grid>
      <TestTable
        tests={tests || []}
        handleChangePage={_handleChangePage}
        handleChangeRowsPerPage={_handleChangeRowsPerPage}
        page={page}
        rowsPerPage={rowsPerPage}
        selected={selected}
        loading={loading || networkStatus === NetworkStatus.fetchMore}
        totaltests={total || 0}
        handleSelectAllClick={_handleSelectAllClick}
        onSelectItem={handleClick}
        onClickItem={handleNavigate}
        onSendMailToSelected={__handleSendMailsToTestPersons}
      />
    </div>
  );
};

export default Tests;
